import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import JobIssueForm from "../../components/service/jobIssueForm.component";
import { useCRUD } from "../../api/crud";
import { JOB_ISSUES_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";

export const JobIssueDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(JOB_ISSUES_ENDPOINT);
  const [updateJobIssueOpened, setUpdateJobIssueOpened] = useState<boolean>(false);
  const [deleteJobIssueOpened, setDeleteJobIssueOpened] = useState<boolean>(false);
  const canChangeJobIssue = useHasPermission("service.change_jobissue");
  const canDeleteJobIssue = useHasPermission("service.delete_jobissue");

  const handleDeleteJobIssue = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Issue deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteJobIssueOpened(false);
      navigate('/service/issues-catalog');
    } else {
      enqueueSnackbar(t('Failed to delete issue'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update issue"),
      onClick: () => {
        setUpdateJobIssueOpened(true);
      },
      disabled: !canChangeJobIssue,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete issue"),
      onClick: () => {
        setDeleteJobIssueOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteJobIssue,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('If this issue is not used in any job, it will be permanently removed from the catalog.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('If this issue is already assigned to a job, it will be deactivated instead: it stays visible in job history but will no longer appear in forms or the catalog list.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteJobIssue}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateJobIssueOpened}
        setOpened={setUpdateJobIssueOpened}
        title={t("Update issue")}
        content={
          <JobIssueForm
            id={id}
            onSuccess={() => {
              setUpdateJobIssueOpened(false);
              window.dispatchEvent(new Event("jobIssueUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteJobIssueOpened}
        setOpened={setDeleteJobIssueOpened}
        title={t("Delete issue")}
        content={deleteModalContent}
      />
    </>
  );
};
